import useActivityObserver from "@ch/helpers/hooks/useActivityObserver";
import useDialogue from "@ch/helpers/hooks/useDialogue";
import useLogout from "@ch/helpers/hooks/useLogout";
import { AppRoutesEnum } from "@ch/router/AppRoutes/enums";
import { type FC, type ReactElement, type ReactNode, useMemo } from "react";
import { useLocation } from "react-router-dom";
import InactivityNotification from "../Inactivity/InactivityNotification";
import Modal from "../Modals/Modal";
import {
  ActivityObserverContext,
  type ActivityObserverContextProps,
} from "./context";

interface ActivityObserverProviderProps {
  isDisabled?: boolean;
  children:
    | ((params: ActivityObserverContextProps) => ReactElement)
    | ReactNode;
}

export const ActivityObserverProvider: FC<ActivityObserverProviderProps> = ({
  children,
  isDisabled = false,
}) => {
  const DISABLED_ON_ROUTES = [AppRoutesEnum.KEM];
  const location = useLocation();
  const isDisabledForRoute =
    isDisabled ||
    DISABLED_ON_ROUTES.some((route) =>
      location.pathname.startsWith(route as string),
    );

  const {
    dialogueId,
    handleCloseDialogue,
    handleOpenDialogue,
    handleUnmountDialogue,
    isDialogueOpen,
  } = useDialogue();

  const {
    resetTimeoutObserver,
    stopTimeoutObserver,
    triggerActivity,
    throttledTriggerActivity,
  } = useActivityObserver({
    onInactive: handleOpenDialogue,
    isDisabled: isDisabledForRoute,
  });

  const handleCancelInactivityNotification = () => {
    if (isDisabledForRoute) {
      stopTimeoutObserver();
    } else {
      resetTimeoutObserver();
    }
  };

  const { logoutUser } = useLogout();

  const handleLogout = () => {
    // Safety check: Don't logout if on a disabled route
    if (isDisabledForRoute) {
      handleCloseDialogue();
      return;
    }

    handleCloseDialogue();
    logoutUser();
  };

  const value: ActivityObserverContextProps = useMemo(() => {
    return {
      triggerActivity,
      throttledTriggerActivity,
    };
  }, [throttledTriggerActivity, triggerActivity]);

  return (
    <ActivityObserverContext.Provider value={value}>
      {typeof children === "function"
        ? children({ triggerActivity, throttledTriggerActivity })
        : children}
      {!isDisabledForRoute && (
        <Modal
          confirmButtonLabel="Stay Active"
          handleConfirm={handleCloseDialogue}
          isModalOpen={isDialogueOpen}
          modalActionClassName="justify-center"
          modalBoxClassName="!h-auto max-w-lg"
          modalId={dialogueId}
          onDismiss={() => {
            handleCancelInactivityNotification();
            handleUnmountDialogue();
          }}
        >
          <InactivityNotification handleLogout={handleLogout} />
        </Modal>
      )}
    </ActivityObserverContext.Provider>
  );
};
